import type { SDKConfig, SDKCommonParams, GTCommonParams, GTParams } from './types';

// Get SDK common parameters
export const getSDKCommonParams = (): SDKCommonParams => {
    const config = getGumtreeConfig();

    return {
        uid: config.userId, // User ID
        devid: config.deviceId || getCookie('gt_p'), // Device ID (httpOnly)
        platform: getPlatform(), // Platform (Web/Mobile)
        browsetype: getBrowserType(), // Browser type
        islogin: config.islogin ? 1 : 0, // Login status: 0 (not logged in), 1 (logged in)
        localtime: getTimestamp(), // Timestamp
        timezone: getTimezone(), // Timezone
        osver: getOSVersion(), // OS version
        surface: getSurface(),
        browserVersion: getBrowserVersion(),
        appVersion: getAppVersion(),
        frontendVersion: getClientFrontendVersion(),
    };
};

// Get business common parameters
export const getGTCommonParams = (eventName: string): GTCommonParams => {
    const config = getGumtreeConfig();

    return {
        event_name: eventName,
        content_group: config.content_group,
        content_type: config.content_type,
        user_pseudo_id: getCookie('_ga'), // GA ID (non-httpOnly)
        session_id: config.sessionId || getCookie('gt_s'), // Session ID (httpOnly)
    };
};

// Get complete GT event parameters
export const getGTParams = (eventName = '', params = {}): GTParams => {
    const gtCommonParams = getGTCommonParams(eventName);
    return {
        ...gtCommonParams,
        ...params,
    };
};

export const serializeString = (data: any, separator: string) => {
    return Object.entries(data)
        .map(([key, value]) => {
            const finalValue = value === undefined || value === null ? '' : value;
            return `${key}=${finalValue}`;
        })
        .join(separator);
};

export const getGumtreeConfig = (): Partial<SDKConfig> => {
    if (typeof window !== 'undefined' && window.__GUMTREE_ANALYTICS_CONFIG__)
        return window.__GUMTREE_ANALYTICS_CONFIG__;
    return {};
};

export const getGA4Data = (): any => {
    if (typeof window === 'undefined') return null;
    return window.__GA4_DATA_LAYER__;
};

export const getCookie = (name: string): string | undefined => {
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
    for (const cookie of cookies) {
        const idx = cookie.indexOf('=');
        const key = cookie.slice(0, idx);
        const value = cookie.slice(idx + 1);
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return undefined;
};

export const getPlatform = (): string => {
    return getPlatformFromUserAgent(navigator.userAgent);
};

export const getPlatformFromUserAgent = (userAgent: string): string => {
    const normalizedUserAgent = userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(normalizedUserAgent)) {
        return 'Mobile';
    }
    return 'Web';
};

export const getBrowserType = (): string => {
    return getBrowserTypeFromUserAgent(navigator.userAgent);
};

export const getBrowserTypeFromUserAgent = (userAgent: string): string => {
    if (/Firefox|FxiOS/.test(userAgent)) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (/Chrome|CriOS/.test(userAgent)) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    return 'Unknown';
};

export const getOSVersion = (): string => {
    return getOSVersionFromUserAgent(navigator.userAgent);
};

export const getOSVersionFromUserAgent = (userAgent: string): string => {
    if (/Windows NT/.test(userAgent)) {
        const match = userAgent.match(/Windows NT (\d+\.\d+)/);
        return match ? `Windows ${match[1]}` : 'Windows';
    }
    if (/iOS|iPhone|iPad|iPod/.test(userAgent)) {
        const match = userAgent.match(/OS (\d+(_\d+)?)/);
        return match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
    }
    if (/Mac OS X/.test(userAgent)) {
        const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
        return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (/Android/.test(userAgent)) {
        const match = userAgent.match(/Android (\d+\.\d+)/);
        return match ? `Android ${match[1]}` : 'Android';
    }
    return 'Unknown';
};

export const getSurfaceFromUserAgent = (
    userAgent: string,
    androidBridge = false,
    iosBridge = false
): NonNullable<SDKCommonParams['surface']> => {
    const android = /Android\/GumtreeApp/i.test(userAgent) || androidBridge;
    const ios = /iOS\/GumtreeApp/i.test(userAgent) || iosBridge;
    if (android && ios) return 'unknown';
    if (android) return 'android_webview';
    if (ios) return 'ios_webview';
    if (/mobile|android|iphone|ipad|ipod/i.test(userAgent)) return 'mobile_web';
    return /Mozilla|Windows|Macintosh|X11|Linux/i.test(userAgent) ? 'desktop_web' : 'unknown';
};

export const getSurface = (): NonNullable<SDKCommonParams['surface']> => {
    try {
        if (typeof window === 'undefined') return 'unknown';
        return getSurfaceFromUserAgent(
            navigator.userAgent,
            typeof (window as any).AndroidBridge?.postMessage === 'function',
            typeof (window as any).webkit?.messageHandlers?.GTNativeAction?.postMessage ===
                'function'
        );
    } catch {
        return 'unknown';
    }
};

export const getValidatedVersion = (value: unknown): string =>
    typeof value === 'string' && value.length <= 32 && /^\d+(?:\.\d+){0,3}$/.test(value)
        ? value
        : 'unknown';

export const getFrontendVersion = (value: unknown): string =>
    typeof value === 'string' && /^[a-f\d]{7,40}$/i.test(value) ? value : 'unknown';

const getClientFrontendVersion = (): string => {
    try {
        return getFrontendVersion(getGumtreeConfig().frontendVersion);
    } catch {
        return 'unknown';
    }
};

export const getBrowserVersionFromUserAgent = (userAgent: string): string => {
    // Match the browser token, never the OS or rendering-engine version.
    const match =
        userAgent.match(/Edg(?:A|iOS)?\/([^\s;)]+)/) ||
        userAgent.match(/(?:Firefox|FxiOS)\/([^\s;)]+)/) ||
        userAgent.match(/(?:Chrome|CriOS)\/([^\s;)]+)/) ||
        (/Safari\//.test(userAgent) ? userAgent.match(/Version\/([^\s;)]+)/) : null);
    return getValidatedVersion(match?.[1]);
};

export const getBrowserVersion = (): string => {
    try {
        return getBrowserVersionFromUserAgent(navigator.userAgent);
    } catch {
        return 'unknown';
    }
};

export const getAppVersion = (): string => {
    const surface = getSurface();
    if (surface !== 'ios_webview' && surface !== 'android_webview') return 'unknown';
    try {
        const versions = new URLSearchParams(window.location.search).getAll('appVersion');
        if (versions.length)
            return versions.length === 1 ? getValidatedVersion(versions[0]) : 'unknown';
        const cookieVersion = getCookie('appVersion');
        if (cookieVersion !== undefined) return getValidatedVersion(cookieVersion);
        return getValidatedVersion(window.localStorage.getItem('appVersion'));
    } catch {
        return 'unknown';
    }
};

export const getNetworkType = (): string => {
    const connection = (navigator as any).connection;
    if (connection) {
        return connection.effectiveType || 'unknown';
    }
    return 'unknown';
};

export const getUTCTime = (): string => {
    return new Date().toISOString();
};

export const getLocalTime = (): string => {
    function getLocalISOTime() {
        const now = new Date();

        const offset = -now.getTimezoneOffset(); // getTimezoneOffset() returns a negative number
        const offsetHours = Math.floor(offset / 60);
        const offsetMinutes = offset % 60;

        // Generate timezone string, e.g., +08:00
        const offsetStr =
            (offsetHours >= 0 ? '+' : '-') +
            String(Math.abs(offsetHours)).padStart(2, '0') +
            ':' +
            String(Math.abs(offsetMinutes)).padStart(2, '0');

        // Convert UTC time to local time
        const localTime = new Date(now.getTime() + offset * 60 * 1000);
        const localISO = localTime.toISOString().replace('Z', '');

        // Output format: 2024-12-30T18:30:45.123+08:00
        return `${localISO}${offsetStr}`;
    }

    return getLocalISOTime();
};

// Get local timestamp (13-digit millisecond timestamp)
export const getTimestamp = (): number => {
    return Date.now();
};

export const getTimezone = (): string => {
    function getUTCOffset() {
        const offset = -new Date().getTimezoneOffset() / 60;
        return `UTC${offset >= 0 ? '+' : ''}${offset}`;
    }
    return getUTCOffset();
};
